import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { Subscription, TRIAL_MODE } from 'src/features/subscriptions/utils/types';
import { subscriptionFormSchema, SubscriptionFormSchemaValues } from 'src/features/subscriptions/utils/formSchema';
import { useGetSubscription } from 'src/features/subscriptions/api/useGetSubscription';
import SubscriptionForm from 'src/features/subscriptions/components/forms/SubscriptionForm';
import { useParams } from 'react-router-dom';

interface SubscriptionFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSubscription?: Subscription;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: SubscriptionFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function SubscriptionFormModal<TData>({
    selectedSubscription,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: SubscriptionFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { customerId = '' } = useParams();

    const { data: subscription } = useGetSubscription({
        id: selectedSubscription?.id ?? '',
        config: { enabled: !!selectedSubscription?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            start_date: subscription?.start_date ?? undefined,
            end_date: subscription?.end_date ?? undefined,
            service_plan: subscription?.service_plan.id ?? undefined,
            customer: subscription?.customer.id ?? customerId ?? undefined,
            has_active_billing: subscription?.has_active_billing ?? undefined,
            trial_mode: TRIAL_MODE.PLAN_DEFAULT,
            trial_end: undefined,
        };
    }, [subscription, customerId]);

    const form = useForm<SubscriptionFormSchemaValues>({
        resolver: zodResolver(subscriptionFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: SubscriptionFormSchemaValues) => {
            const { trial_mode, trial_end, ...rest } = values;

            /*
             * Omitted, explicit-null and an explicit date mean three different things to the
             * backend: use the plan's trial_period_days, no trial at all, or this exact end date.
             * trial_end is writable on create only, so an update sends neither key.
             */
            const payload = selectedSubscription
                ? rest
                : {
                      ...rest,
                      ...(trial_mode === TRIAL_MODE.CUSTOM && { trial_end }),
                      ...(trial_mode === TRIAL_MODE.NONE && { trial_end: null }),
                  };

            mutate(
                payload as SubscriptionFormSchemaValues,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, onError, form, selectedSubscription],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <SubscriptionForm form={form} onSubmit={onSubmit} isUpdate={Boolean(selectedSubscription)} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedSubscription && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default SubscriptionFormModal;
